import type { AsyncDataOptions } from '#app'
import { AsyncDataKeys } from '@/constants/async-data-keys'

import { getBranchArrivalTimes, getBranchPAA, getBranchReviewSummary } from '@/service/branch'
import type { BranchArrivalTimeInfo, BranchData, DomainReviewSummaryResult } from '@/types/api'

interface UseGetBranchArrivalTimesOpts {
  branch: ReadonlyRef<Nullable<BranchData>>
  attendance: ReadonlyRef<number>
  callback?: (data: BranchArrivalTimeInfo) => void
  asyncDataOpts?: AsyncDataOptions<BranchArrivalTimeInfo>
}

export async function useGetBranchArrivalTimes(prefixKey: string, { branch, attendance, callback, asyncDataOpts }: UseGetBranchArrivalTimesOpts) {
  const route = useRoute()

  const branchId = computed(() => route.params.bid as string)
  const productId = computed(() => branch.value?.pid)

  const asyncData = await useAsyncData(
    useAsyncDataKey(AsyncDataKeys.BRANCH_ARRIVAL_TIMES, [prefixKey, branchId.value, productId.value, attendance.value], { withLocale: false }),
    async () => {
      if (!branchId.value) {
        throw new Error('branchId is required')
      }
      const queryParams = { pid: productId.value, headcount: attendance.value }
      const { data } = await getBranchArrivalTimes(branchId.value, queryParams)

      if (typeof callback === 'function') {
        callback(data)
      }

      return data
    },
    asyncDataOpts
  )

  const isBranchArrivalTimesLoading = computed(() => asyncData.status.value === 'pending')

  return { ...asyncData, isBranchArrivalTimesLoading }
}

export async function useGetBranchReviewSummary(branchId: ReadonlyRef<string>) {
  const asyncData = await useAsyncData(useAsyncDataKey(AsyncDataKeys.BRANCH_REVIEW_SUMMARY, [branchId.value]), async () => {
    if (!branchId.value) {
      throw new Error('branchId is required')
    }

    const { data } = await getBranchReviewSummary(branchId.value)

    return data as DomainReviewSummaryResult
  })

  return asyncData
}

export async function useGetBranchPAA(branchId: ReadonlyRef<string>) {
  const asyncData = await useAsyncData(useAsyncDataKey(AsyncDataKeys.BRANCH_PAA, [branchId.value]), async () => {
    if (!branchId.value) {
      throw new Error('branchId is required')
    }

    const { data } = await getBranchPAA(branchId.value)

    return data
  })

  return asyncData
}
